// Builds the ONE thing an operator pastes into a freshly-flashed robot's shell.
//
// WHY THIS EXISTS. The dialog used to show only `ROBOT_ID=... sh bringup.sh`, which
// presumes bringup.sh is already on the robot. Nothing put it there: on a virgin Jetson
// that command fails with "Can't open bringup.sh". The script was reachable only by
// hand-selecting a 135-line textarea in the Catalog editor, a step documented nowhere.
// So the block below WRITES the script and then runs it -- one paste, no editing.
//
// The token is still not in here. It is prompted from /dev/tty, because a one-time
// credential on a command line lands in shell history and is visible in `ps`.

export interface InstallBlockInput {
  script: string;
  robotId: string;
  apiBase: string;
}

const BASE_DELIMITER = 'BRINGUP_EOF';

// A quoted heredoc terminates on a line that is EXACTLY the delimiter, so a script
// containing such a line would be truncated at it and the remainder would be executed as
// shell. Our own script does not, but the Catalog editor lets an operator write anything,
// and the failure mode is a half-written script running as root. Pick a delimiter the
// body does not contain rather than trusting it.
export function pickDelimiter(lines: readonly string[]): string {
  if (!lines.some((l) => l.trim() === BASE_DELIMITER)) return BASE_DELIMITER;
  for (let n = 2; ; n += 1) {
    const candidate = `${BASE_DELIMITER}_${n}`;
    if (!lines.some((l) => l.trim() === candidate)) return candidate;
  }
}

export function buildInstallBlock({ script, robotId, apiBase }: InstallBlockInput): string {
  // CR stripped, not preserved. A script that reached the editor through a Windows
  // clipboard carries CRLF, the heredoc would write it verbatim, and `sh` fails on the
  // trailing \r with errors that name the right line and look like nonsense
  // ("bringup.sh: 4: set: Illegal option - "). Cheap to normalise, miserable to debug.
  const lines = script.replace(/\r\n?/g, '\n').split('\n');
  // A trailing empty element from the final newline would add a blank line before the
  // delimiter; harmless, but it makes the copied block look sloppy.
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

  const delimiter = pickDelimiter(lines);

  // 'QUOTED' delimiter: with it bare, the shell would expand $VAR, `cmd` and \ inside the
  // body AS IT WROTE THE FILE -- so ${ENROLL_TOKEN:?} and every $(...) in the script would
  // be substituted against the OPERATOR's empty environment and written out blank.
  return [
    `cat > bringup.sh <<'${delimiter}'`,
    ...lines,
    delimiter,
    `ROBOT_ID=${robotId} API=${apiBase} sh bringup.sh`,
    '',
  ].join('\n');
}
